import { compact, round, sumBy } from 'lodash';
import getHoldingClassRealisation from './get-holding-class-realisation';

import getInvestorsWithholdingTax from './get-investors-withholding-tax';
import {
  WithholdingTaxAllocation,
  MinimumHoldingClassRealisation,
} from 'further-types/lp/realisation';

const calculateTotalConsideration = (
  units: number | undefined,
  considerationPerUnit: number,
): number => {
  return round((units ?? 0) * considerationPerUnit, 2);
};

const calculateWithholdingTax = (
  totalWithholdingTax: number,
  percentage: number,
): number => {
  return totalWithholdingTax * percentage;
};

const getHoldingClassInvestmentsConsiderationValues = ({
  holdingClassRealisations,
  withholdingTaxList,
}: {
  holdingClassRealisations: Array<MinimumHoldingClassRealisation>;
  withholdingTaxList: Array<WithholdingTaxAllocation>;
}) => {
  const allHoldingClassInvestments = holdingClassRealisations
    .flatMap(
      (holdingClassRealisation) =>
        holdingClassRealisation.unitsAllocationPerInvestment,
    )
    .filter((investment) => investment?.allocatedUnits ?? 0 > 0);

  const investments = compact(allHoldingClassInvestments).map(
    (holdingClassInvestment) => {
      const holdingClassId = holdingClassInvestment?.holdingClassId;
      const allocatedUnits = holdingClassInvestment?.allocatedUnits;
      const investmentId = holdingClassInvestment?.investmentId;
      const date = holdingClassInvestment?.date;
      const currencyRate = holdingClassInvestment?.currencyRate;

      const { unitsSold, cashConsiderationAmounts, name } =
        getHoldingClassRealisation(
          holdingClassRealisations,
          holdingClassInvestment?.holdingClassId,
        ) ?? {};

      const allocatedPercentage =
        unitsSold && unitsSold > 0 ? (allocatedUnits ?? 0) / unitsSold : 0;

      const investorsWithholdingTax =
        getInvestorsWithholdingTax(withholdingTaxList);

      const taxAllocated = holdingClassInvestment.allocations?.filter(
        (investor) =>
          investorsWithholdingTax?.find(
            (withholdingTaxInvestor) =>
              withholdingTaxInvestor.commitmentId === investor.commitmentId,
          ),
      );

      const taxAllocatedPercentage = sumBy(
        taxAllocated,
        'allocationPercentage',
      );

      return {
        holdingClassId,
        investmentId,
        investmentDate: date,
        holdingClassName: name,
        unitsRealised: allocatedUnits ?? 0,
        originalInvestmentCurrencyRate: currencyRate,
        receivedConsiderationPerUnit:
          cashConsiderationAmounts?.receivedConsiderationPerUnit ?? 0,
        receivedConsiderationTotal: calculateTotalConsideration(
          allocatedUnits,
          cashConsiderationAmounts?.receivedConsiderationPerUnit ?? 0,
        ),
        escrowConsiderationPerUnit:
          cashConsiderationAmounts?.escrowConsiderationPerUnit ?? 0,
        escrowConsiderationTotal: calculateTotalConsideration(
          allocatedUnits,
          cashConsiderationAmounts?.escrowConsiderationPerUnit ?? 0,
        ),
        earnoutConsiderationPerUnit:
          cashConsiderationAmounts?.earnoutConsiderationPerUnit ?? 0,
        earnoutConsiderationTotal: calculateTotalConsideration(
          allocatedUnits,
          cashConsiderationAmounts?.earnoutConsiderationPerUnit ?? 0,
        ),
        nonTaxExemptAllocationPercentage: taxAllocatedPercentage,
        withholdingTaxPayable: calculateWithholdingTax(
          cashConsiderationAmounts?.withholdingTaxPayable ?? 0,
          allocatedPercentage,
        ),
      };
    },
  );

  return investments;
};

export default getHoldingClassInvestmentsConsiderationValues;
